import React, { useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useNavigate, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";

/* -------------------- Animated Background Grid -------------------- */
const GridBackground = () => (
  <div className="absolute inset-0 overflow-hidden pointer-events-none">
    <div
      className="absolute inset-0 opacity-[0.05]"
      style={{
        backgroundImage: `
          linear-gradient(rgba(38, 49, 59, 0.5) 1px, transparent 1px),
          linear-gradient(90deg, rgba(38, 49, 59, 0.5) 1px, transparent 1px)
        `,
        backgroundSize: "50px 50px",
      }}
    />
  </div>
);

/* -------------------- Floating Orbs Background -------------------- */
const FloatingOrbs = () => (
  <div className="absolute inset-0 overflow-hidden pointer-events-none">
    <motion.div
      className="absolute w-[600px] h-[600px] rounded-full"
      style={{
        background:
          "radial-gradient(circle, rgba(232, 180, 76, 0.25) 0%, transparent 70%)",
        left: "-10%",
        top: "-20%",
      }}
      animate={{ x: [0, 40, 0], y: [0, 30, 0] }}
      transition={{ duration: 20, repeat: Infinity, ease: "easeInOut" }}
    />
    <motion.div
      className="absolute w-[500px] h-[500px] rounded-full"
      style={{
        background:
          "radial-gradient(circle, rgba(168, 114, 14, 0.12) 0%, transparent 70%)",
        right: "-5%",
        bottom: "-10%",
      }}
      animate={{ x: [0, -40, 0], y: [0, -30, 0] }}
      transition={{ duration: 25, repeat: Infinity, ease: "easeInOut" }}
    />
    <motion.div
      className="absolute w-[400px] h-[400px] rounded-full"
      style={{
        background:
          "radial-gradient(circle, rgba(15, 107, 191, 0.10) 0%, transparent 70%)",
        left: "40%",
        top: "60%",
      }}
      animate={{ x: [0, 30, 0], y: [0, -40, 0] }}
      transition={{ duration: 18, repeat: Infinity, ease: "easeInOut" }}
    />
  </div>
);

/* -------------------- Login Component -------------------- */
export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [focusedField, setFocusedField] = useState(null);

  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      await login(email, password);
      navigate("/admin");
    } catch (err) {
      setError(
        err.message === "Unauthorized: You are not an admin"
          ? "Access denied. Admin only."
          : "Invalid credentials."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FBFAF6] flex items-center justify-center px-4 sm:px-6 relative overflow-hidden">
      <GridBackground />
      <FloatingOrbs />

      {/* Scan line effect */}
      <motion.div
        className="absolute inset-0 pointer-events-none opacity-[0.02]"
        style={{
          background:
            "repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(38,49,59,0.03) 2px, rgba(38,49,59,0.03) 4px)",
        }}
      />

      <motion.div
        initial={{ opacity: 0, y: 30, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
        className="w-full max-w-md relative z-10"
      >
        {/* Glowing border effect */}
        <div className="absolute -inset-[1px] bg-gradient-to-r from-[#E8B44C] via-[#D9A32E] to-[#E8B44C] rounded-3xl opacity-40 blur-sm" />
        <div className="absolute -inset-[1px] bg-gradient-to-r from-[#E8B44C] via-[#D9A32E] to-[#E8B44C] rounded-3xl opacity-30" />

        <div className="relative bg-white/95 backdrop-blur-xl rounded-3xl p-8 border border-[#E1DCCE]">
          {/* Terminal-style header */}
          <div className="flex items-center gap-2 mb-8 pb-4 border-b border-[#E1DCCE]">
            <div className="flex gap-1.5">
              <div className="w-3 h-3 rounded-full bg-red-500/80" />
              <div className="w-3 h-3 rounded-full bg-yellow-500/80" />
              <div className="w-3 h-3 rounded-full bg-green-500/80" />
            </div>
            <span className="text-xs text-[#8A93A2] font-mono ml-2">
              cosmovex://auth/admin
            </span>
          </div>

          {/* Logo and Title */}
          <div className="text-center mb-8">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
              className="relative inline-block mb-4"
            >
              <div className="w-16 h-16 bg-gradient-to-br from-[#E8B44C] via-[#D9A32E] to-[#A8720E] rounded-2xl flex items-center justify-center shadow-2xl shadow-[#E8B44C]/40">
                <span className="text-3xl font-black text-[#221A06]">C</span>
              </div>
              <motion.div
                className="absolute -inset-1 bg-gradient-to-br from-[#E8B44C] to-[#D9A32E] rounded-2xl opacity-40 blur-md -z-10"
                animate={{ scale: [1, 1.1, 1], opacity: [0.3, 0.5, 0.3] }}
                transition={{ duration: 3, repeat: Infinity }}
              />
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="text-3xl font-bold text-[#26313B] mb-2"
            >
              Admin{" "}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#A8720E] to-[#D9A32E]">
                Portal
              </span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.4 }}
              className="text-sm text-[#8A93A2] font-mono"
            >
              Secure authentication required
            </motion.p>
          </div>

          {/* Error Alert */}
          <AnimatePresence>
            {error && (
              <motion.div
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -10 }}
                className="mb-6 p-4 bg-[#C24E4E]/10 border border-[#C24E4E]/30 rounded-xl backdrop-blur-sm"
              >
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-[#C24E4E]/15 flex items-center justify-center flex-shrink-0">
                    <svg
                      className="w-4 h-4 text-[#C24E4E]"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                      />
                    </svg>
                  </div>
                  <span className="text-[#C24E4E] text-sm">{error}</span>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Email Field */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.5 }}
            >
              <label className="block text-sm font-medium text-[#5B6B7C] mb-2 flex items-center gap-2">
                <svg
                  className="w-4 h-4 text-[#A8720E]"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207"
                  />
                </svg>
                Email Address
              </label>
              <div
                className={`relative group ${
                  focusedField === "email" ? "z-10" : ""
                }`}
              >
                <div
                  className={`absolute -inset-[1px] bg-gradient-to-r from-[#E8B44C] to-[#D9A32E] rounded-xl opacity-0 group-hover:opacity-50 transition-opacity blur-sm ${
                    focusedField === "email" ? "opacity-75" : ""
                  }`}
                />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onFocus={() => setFocusedField("email")}
                  onBlur={() => setFocusedField(null)}
                  required
                  className="relative w-full px-4 py-3.5 bg-white border border-[#E1DCCE] rounded-xl text-[#26313B] placeholder-[#8A93A2] focus:outline-none focus:border-[#D9A32E] transition-all font-mono text-sm"
                  placeholder="admin@cosmovex.com"
                />
              </div>
            </motion.div>

            {/* Password Field */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.6 }}
            >
              <label className="block text-sm font-medium text-[#5B6B7C] mb-2 flex items-center gap-2">
                <svg
                  className="w-4 h-4 text-[#A8720E]"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                  />
                </svg>
                Password
              </label>
              <div
                className={`relative group ${
                  focusedField === "password" ? "z-10" : ""
                }`}
              >
                <div
                  className={`absolute -inset-[1px] bg-gradient-to-r from-[#E8B44C] to-[#D9A32E] rounded-xl opacity-0 group-hover:opacity-50 transition-opacity blur-sm ${
                    focusedField === "password" ? "opacity-75" : ""
                  }`}
                />
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onFocus={() => setFocusedField("password")}
                  onBlur={() => setFocusedField(null)}
                  required
                  className="relative w-full px-4 py-3.5 bg-white border border-[#E1DCCE] rounded-xl text-[#26313B] placeholder-[#8A93A2] focus:outline-none focus:border-[#D9A32E] transition-all font-mono text-sm"
                  placeholder="••••••••••••"
                />
              </div>
            </motion.div>

            {/* Submit Button */}
            <motion.button
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7 }}
              type="submit"
              disabled={loading}
              className="relative w-full group mt-6"
            >
              <div className="absolute -inset-[1px] bg-gradient-to-r from-[#E8B44C] via-[#D9A32E] to-[#E8B44C] rounded-xl opacity-50 group-hover:opacity-75 transition-opacity blur-sm group-hover:blur-md" />
              <div className="relative px-6 py-4 bg-gradient-to-r from-[#E8B44C] via-[#DFA93C] to-[#E8B44C] border border-[#D9A32E] text-[#221A06] font-semibold rounded-xl flex items-center justify-center gap-2 transition-all group-hover:shadow-lg group-hover:shadow-[#E8B44C]/40 disabled:opacity-50 disabled:cursor-not-allowed">
                {loading ? (
                  <>
                    <motion.div
                      className="w-5 h-5 border-2 border-[#221A06]/30 border-t-[#221A06] rounded-full"
                      animate={{ rotate: 360 }}
                      transition={{
                        duration: 1,
                        repeat: Infinity,
                        ease: "linear",
                      }}
                    />
                    <span>Authenticating...</span>
                  </>
                ) : (
                  <>
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1"
                      />
                    </svg>
                    <span>Access Portal</span>
                  </>
                )}
              </div>
            </motion.button>
          </form>

          {/* Footer */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.8 }}
            className="mt-8 pt-6 border-t border-[#E1DCCE] text-center"
          >
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-sm text-[#8A93A2] hover:text-[#A8720E] transition-colors group"
            >
              <svg
                className="w-4 h-4 group-hover:-translate-x-1 transition-transform"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M10 19l-7-7m0 0l7-7m-7 7h18"
                />
              </svg>
              Return to Cosmovex
            </Link>
          </motion.div>

          {/* Decorative corner elements */}
          <div className="absolute top-0 right-0 w-20 h-20 pointer-events-none">
            <div className="absolute top-4 right-4 w-2 h-8 border-t-2 border-r-2 border-[#D9A32E]/40 rounded-tr-lg" />
          </div>
          <div className="absolute bottom-0 left-0 w-20 h-20 pointer-events-none">
            <div className="absolute bottom-4 left-4 w-2 h-8 border-b-2 border-l-2 border-[#D9A32E]/40 rounded-bl-lg" />
          </div>
        </div>
      </motion.div>

      {/* Bottom status bar */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1 }}
        className="absolute bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-4 text-xs text-[#8A93A2] font-mono"
      >
        <span className="flex items-center gap-1.5">
          <span className="w-1.5 h-1.5 rounded-full bg-[#2E7D4F] animate-pulse" />
          Secure Connection
        </span>
        <span>•</span>
        <span>TLS 1.3 Encrypted</span>
        <span>•</span>
        <span>v2.0.0</span>
      </motion.div>
    </div>
  );
}
